import { useMemo } from 'react';
import { z } from 'zod';
import { useBookkeepingAccountsByCodes } from '~/services/bookkeeping/bookkeeping-accounts';
import { useBookkeepingSettings } from '~/services/company-settings';
import { BookkeepingAccountsSettings, VATPeriodAllocationLogic } from '~/types/company-settings';

export interface BookkeepingConfiguration {
  accounts: BookkeepingAccountsSettings;
  vatPeriodAllocationLogic: VATPeriodAllocationLogic | null;
}

export const useBookkeepingConfiguration = (): BookkeepingConfiguration => {
  const settings = useBookkeepingSettings();
  const {
    data: { accounts: allAccounts },
  } = useBookkeepingAccountsByCodes(
    settings?.accounts
      ? [
          settings.accounts.debtsSuppliers,
          settings.accounts.debtsForeignSuppliers,
          settings.accounts.creditsClients,
          settings.accounts.creditsForeignClients,
          settings.accounts.vatSales,
          settings.accounts.vatPurchases,
          settings.accounts.bankFees,
          settings.accounts.netBills,
          settings.accounts.vatBills,
          settings.accounts.billsCash,
          settings.accounts.billsCashContanti,
          settings.accounts.billsCashElectronic,
          settings.accounts.billsCashTicket,
          settings.accounts.billsRevenueTicket,
          settings.accounts.billsUnredeemedGift,
          settings.accounts.revenueAccount,
          settings.accounts.withholdingAccount,
          settings.accounts.withholdingAccountSales,
          settings.accounts.activeRoundingAccount,
          settings.accounts.passiveRoundingAccount,
          settings.accounts.vatSalesSplitPayment,
          settings.accounts.previousYearVatCredit,
          settings.accounts.vatSettlement,
          settings.accounts.quarterlyVatInterest,
          settings.accounts.socialSecuritySales,
          settings.accounts.taxReturnVatRefund,
          settings.accounts.taxReturnVatCompensation,
        ]
      : [],
  );

  return useMemo(() => {
    const accountByCode = new Map(allAccounts.map((account) => [account.code, account.id]));
    return {
      accounts: {
        debtsSuppliers: accountByCode.get(settings?.accounts?.debtsSuppliers ?? '') || null,
        debtsForeignSuppliers: accountByCode.get(settings?.accounts?.debtsForeignSuppliers ?? '') || null,
        creditsClients: accountByCode.get(settings?.accounts?.creditsClients ?? '') || null,
        creditsForeignClients: accountByCode.get(settings?.accounts?.creditsForeignClients ?? '') || null,
        vatSales: accountByCode.get(settings?.accounts?.vatSales ?? '') || null,
        vatPurchases: accountByCode.get(settings?.accounts?.vatPurchases ?? '') || null,
        bankFees: accountByCode.get(settings?.accounts?.bankFees ?? '') || null,
        netBills: accountByCode.get(settings?.accounts?.netBills ?? '') || null,
        vatBills: accountByCode.get(settings?.accounts?.vatBills ?? '') || null,
        billsCash: accountByCode.get(settings?.accounts?.billsCash ?? '') || null,
        billsCashContanti: accountByCode.get(settings?.accounts?.billsCashContanti ?? '') || null,
        billsCashElectronic: accountByCode.get(settings?.accounts?.billsCashElectronic ?? '') || null,
        billsCashTicket: accountByCode.get(settings?.accounts?.billsCashTicket ?? '') || null,
        billsRevenueTicket: accountByCode.get(settings?.accounts?.billsRevenueTicket ?? '') || null,
        billsUnredeemedGift: accountByCode.get(settings?.accounts?.billsUnredeemedGift ?? '') || null,
        revenueAccount: accountByCode.get(settings?.accounts?.revenueAccount ?? '') || null,
        withholdingAccount: accountByCode.get(settings?.accounts?.withholdingAccount ?? '') || null,
        withholdingAccountSales: accountByCode.get(settings?.accounts?.withholdingAccountSales ?? '') || null,
        activeRoundingAccount: accountByCode.get(settings?.accounts?.activeRoundingAccount ?? '') || null,
        passiveRoundingAccount: accountByCode.get(settings?.accounts?.passiveRoundingAccount ?? '') || null,
        vatSalesSplitPayment: accountByCode.get(settings?.accounts?.vatSalesSplitPayment ?? '') || null,
        previousYearVatCredit: accountByCode.get(settings?.accounts?.previousYearVatCredit ?? '') || null,
        vatSettlement: accountByCode.get(settings?.accounts?.vatSettlement ?? '') || null,
        quarterlyVatInterest: accountByCode.get(settings?.accounts?.quarterlyVatInterest ?? '') || null,
        socialSecuritySales: accountByCode.get(settings?.accounts?.socialSecuritySales ?? '') || null,
        taxReturnVatRefund: accountByCode.get(settings?.accounts?.taxReturnVatRefund ?? '') || null,
        taxReturnVatCompensation: accountByCode.get(settings?.accounts?.taxReturnVatCompensation ?? '') || null,
      },
      vatPeriodAllocationLogic: settings?.vatPeriodAllocationLogic ?? VATPeriodAllocationLogic.ReceiptDate,
    };
  }, [settings, allAccounts]);
};

const completeBookkeepingSettingsSchema = z.object({
  accounts: z.object({
    debtsSuppliers: z.string(),
    debtsForeignSuppliers: z.string().nullable(),
    creditsClients: z.string(),
    creditsForeignClients: z.string().nullable(),
    vatSales: z.string(),
    vatPurchases: z.string(),
    bankFees: z.string().nullable(),
    billsCash: z.string().nullable(),
    billsCashContanti: z.string().nullable(),
    billsCashElectronic: z.string().nullable(),
    billsCashTicket: z.string().nullable(),
    billsRevenueTicket: z.string().nullable(),
    billsUnredeemedGift: z.string().nullable(),
    netBills: z.string(),
    vatBills: z.string(),
    revenueAccount: z.string(),
    withholdingAccount: z.string().nullable(),
    withholdingAccountSales: z.string().nullable(),
    activeRoundingAccount: z.string().nullable(),
    passiveRoundingAccount: z.string().nullable(),
    vatSalesSplitPayment: z.string().nullable(),
    previousYearVatCredit: z.string().nullable(),
    vatSettlement: z.string().nullable(),
    quarterlyVatInterest: z.string().nullable(),
    socialSecuritySales: z.string().nullable(),
    taxReturnVatRefund: z.string().nullable(),
    taxReturnVatCompensation: z.string().nullable(),
  }),
});

export const useIsBookkeepingConfigurationValid = () => {
  const configuration = useBookkeepingConfiguration();
  const parsedSettings = completeBookkeepingSettingsSchema.safeParse(configuration);
  return parsedSettings.success;
};
